import { put, takeLatest } from 'redux-saga/effects'
import fileDownload from 'js-file-download'
import { notification } from 'antd'

import * as actions from './actions'
import actionTypes from './actionTypes'
import i18next from 'i18next'
import reportError from '../reportError'
import restUtils from '../restUtils'

const endpoints = {
  vessel: 'vessel',
}

// Fetch vessels without pagination (filtering happens on frontend)
function* requestVessels(action) {
  try {
    yield put(actions.setRequestingVessels(true))

    // Fetch all vessels without pagination query parameters
    const endpoint = endpoints.vessel
    const response = yield restUtils.getRequest(endpoint)

    if (response && response.ok) {
      const responseBody = response.body
      let vesselsData =
        responseBody?.data !== undefined ? responseBody.data : responseBody

      // Convert single vessel object to array if needed
      if (vesselsData && !Array.isArray(vesselsData)) {
        vesselsData = [vesselsData]
      }

      // Pass the search query to the success action for frontend filtering
      const { page, pageSize, searchQuery } = action.payload
      yield put(
        actions.vesselsRequestSuccessful({
          page,
          pageSize,
          searchQuery,
          vessels: vesselsData,
        })
      )
    } else {
      yield put(actions.vesselsRequestUnsuccessful())

      notification['error']({
        description: i18next.t('global.error.getRequestErrorDescription'),
        message: i18next.t('global.error.getRequestErrorMessage'),
      })

      reportError(
        `Response unsuccessful for vessels. Status code: ${response?.status}`
      )
    }
  } catch (error) {
    yield put(actions.vesselsRequestUnsuccessful())

    if (!error?.isAuthError) {
      notification['error']({
        description: i18next.t('global.error.getRequestErrorDescription'),
        message: i18next.t('global.error.getRequestErrorMessage'),
      })
    }

    reportError(`Failed to fetch vessels: ${error}`)
  } finally {
    yield put(actions.setRequestingVessels(false))
  }
}

export function* watchRequestVessels() {
  yield takeLatest(actionTypes.REQUEST_VESSELS, requestVessels)
}

// Fetch single vessel by IMO number or ID
function* requestVessel(action) {
  try {
    yield put(actions.setRequestingVessel(true))

    const { imoNumber } = action.payload

    // Use imoNumber as the identifier (could be id or imoNumber)
    const endpoint = `${endpoints.vessel}/${imoNumber}`
    const response = yield restUtils.getRequest(endpoint)

    if (response && response.ok) {
      const responseBody = response.body
      const vesselData =
        responseBody?.data !== undefined ? responseBody.data : responseBody

      yield put(actions.vesselRequestSuccessful(vesselData))
    } else {
      yield put(actions.vesselRequestUnsuccessful())

      notification['error']({
        description: i18next.t('global.error.getRequestErrorDescription'),
        message: i18next.t('global.error.getRequestErrorMessage'),
      })

      reportError(
        `Response unsuccessful for vessel ${imoNumber}. Status code: ${response?.status}`
      )
    }
  } catch (error) {
    yield put(actions.vesselRequestUnsuccessful())

    if (!error?.isAuthError) {
      notification['error']({
        description: i18next.t('global.error.getRequestErrorDescription'),
        message: i18next.t('global.error.getRequestErrorMessage'),
      })
    }

    reportError(`Failed to fetch vessel: ${error}`)
  } finally {
    yield put(actions.setRequestingVessel(false))
  }
}

export function* watchRequestVessel() {
  yield takeLatest(actionTypes.REQUEST_VESSEL, requestVessel)
}

// Unified save vessel details (following arrival/departure pattern)
function* saveVesselDetailsSaga({ payload }) {
  const { type, payload: body, onSuccess, onError } = payload
  const SUCCESS_STATUS_CODE_OK = 200
  const SUCCESS_STATUS_CODE_CREATED = 201

  yield put(actions.setRequestingSaveVesselDetails(true))
  yield put(actions.setSaveVesselDetailsError(null))

  try {
    let successMessage = ''
    let response

    // Choose the right HTTP method based on type
    switch (type) {
      case 'CREATE':
        response = yield restUtils.postRequest(endpoints.vessel, body)
        successMessage = `Vessel "${body.vesselName}" has been created successfully.`
        break

      case 'UPDATE':
        response = yield restUtils.putRequest(endpoints.vessel, body)
        successMessage = 'Vessel has been updated successfully.'
        break

      case 'CLONE':
        response = yield restUtils.postRequest(endpoints.vessel, body)
        successMessage = `Vessel "${body.vesselName}" has been cloned successfully.`
        break

      default:
        throw new Error(`Unknown vessel operation type: ${type}`)
    }

    if (
      response?.status === SUCCESS_STATUS_CODE_OK ||
      response?.status === SUCCESS_STATUS_CODE_CREATED
    ) {
      // Also check backend envelope code if present
      const envelopeCode = response?.body?.code
      const isBackendSuccess =
        envelopeCode === undefined ||
        envelopeCode === '200' ||
        envelopeCode === 200

      if (!isBackendSuccess) {
        const backendMessage =
          response?.body?.message ||
          `Failed to ${type.toLowerCase()} vessel. Please try again.`

        if (onError) {
          onError(backendMessage)
        } else {
          notification['error']({
            description: backendMessage,
            message: `${type.charAt(0) + type.slice(1).toLowerCase()} Failed`,
          })

          reportError(
            `Save vessel failed (${type}). Envelope code: ${envelopeCode}. Message: ${backendMessage}`
          )
        }

        return
      }

      // Show success notification
      notification['success']({
        description: successMessage,
        message:
          type === 'CREATE'
            ? 'Vessel Created'
            : type === 'CLONE'
              ? 'Vessel Cloned'
              : 'Vessel Updated',
      })

      // Refresh vessel list after successful save
      yield put(actions.requestVessels(1, 10, ''))

      if (onSuccess) {
        // UI callback — closes the drawer
        onSuccess()
      }
    } else {
      // Extract backend error message if available
      const backendMessage =
        response?.body?.message ||
        response?.body?.error ||
        `Failed to ${type.toLowerCase()} vessel. Please try again.`

      if (onError) {
        onError(backendMessage)
      } else {
        notification['error']({
          description: backendMessage,
          message: `${type.charAt(0) + type.slice(1).toLowerCase()} Failed`,
        })

        reportError(
          `Save vessel failed (${type}). Status: ${response?.status}. Message: ${backendMessage}`
        )
      }
    }
  } catch (error) {
    const errorMessage = `Failed to ${type.toLowerCase()} vessel. Please try again.`

    if (onError) {
      onError(errorMessage)
    } else {
      notification['error']({
        description: errorMessage,
        message: `${type.charAt(0) + type.slice(1).toLowerCase()} Failed`,
      })

      reportError(`Failed to ${type.toLowerCase()} vessel details: ${error}`)
    }
  } finally {
    yield put(actions.setRequestingSaveVesselDetails(false))
  }
}

export function* watchSaveVesselDetails() {
  yield takeLatest(actionTypes.SAVE_VESSEL_DETAILS, saveVesselDetailsSaga)
}

// Export vessel
function* exportVessel(action) {
  const { id } = action.payload

  const response = yield restUtils.exportVessel(id)

  if (response && response.body) {
    // Default filename if we can't extract from headers
    let filename = `vessel-config-${id}-${Date.now()}.json`

    // Try to get filename from Content-Disposition header
    const headers = response.header || response.headers
    if (headers) {
      console.log('All headers:', headers)
      const contentDisposition =
        headers['content-disposition'] || headers['Content-Disposition']
      console.log('Content-Disposition header value:', contentDisposition)
      if (contentDisposition) {
        // Match filename from Content-Disposition header
        // Handles formats like: attachment; filename="filename.json" or attachment; filename=filename.json
        const filenameMatch = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        )
        console.log('Filename match result:', filenameMatch)
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '')
          console.log('Extracted filename:', filename)
        }
      }
    }

    // Convert JSON to string for download
    const jsonString = JSON.stringify(response.body, null, 2)

    fileDownload(jsonString, filename)

    notification['success']({
      description: `Vessel data has been successfully exported.`,
      message: 'Vessel Exported',
    })
  }
}

export function* watchExportVessel() {
  yield takeLatest(actionTypes.EXPORT_VESSEL, exportVessel)
}
